import { useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import {
  Users, Euro, Zap, Link2, Unlink, Search, ChevronDown, ChevronUp, UserPlus
} from 'lucide-react';

export const AdminSponsorshipsTab = ({
  sponsorships, prosList, unsponsoredUsers,
  onAssignSponsor, onRemoveSponsor, onSearchUnsponsored, loading
}) => {
  const [expanded, setExpanded] = useState(null);
  const [assignMode, setAssignMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPro, setSelectedPro] = useState('');

  const handleSearch = (q) => {
    setSearchQuery(q);
    if (q.length >= 2) onSearchUnsponsored(q);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold">Gestion des parrainages</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            {sponsorships.length} professionnel{sponsorships.length > 1 ? 's' : ''} actif{sponsorships.length > 1 ? 's' : ''}
          </p>
        </div>
        <Button
          onClick={() => setAssignMode(!assignMode)}
          className={assignMode ? 'bg-slate-600 hover:bg-slate-700 text-white rounded-full px-5' : 'bg-green-600 hover:bg-green-700 text-white rounded-full px-5'}
          data-testid="toggle-assign-mode"
        >
          <Link2 className="w-4 h-4 mr-2" />
          {assignMode ? 'Fermer' : 'Assigner un parrain'}
        </Button>
      </div>

      {/* Assign sponsor panel */}
      {assignMode && (
        <Card className="border-2 border-green-200 bg-green-50/30" data-testid="assign-sponsor-panel">
          <CardContent className="p-5 space-y-4">
            <h4 className="font-semibold text-sm">Assigner un parrain a un utilisateur</h4>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">Professionnel (parrain)</label>
                <select
                  value={selectedPro}
                  onChange={(e) => setSelectedPro(e.target.value)}
                  className="w-full border rounded-xl px-3 py-2 text-sm bg-white"
                  data-testid="select-pro-sponsor"
                >
                  <option value="">Choisir un professionnel...</option>
                  {prosList.map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.email})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">Rechercher un utilisateur</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Nom ou email..."
                    className="pl-9"
                    data-testid="search-unsponsored"
                  />
                </div>
              </div>
            </div>
            {unsponsoredUsers.length > 0 && searchQuery.length >= 2 && (
              <div className="border rounded-xl overflow-hidden bg-white">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Utilisateur</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Type</th>
                      <th className="text-right py-2 px-3 font-medium text-muted-foreground">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unsponsoredUsers.map(u => (
                      <tr key={u.id} className="border-b hover:bg-muted/30" data-testid={`unsponsored-user-${u.id}`}>
                        <td className="py-2 px-3">
                          <p className="font-medium">{u.name}</p>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        </td>
                        <td className="py-2 px-3">
                          <Badge className={u.user_type === 'producer' ? 'bg-green-100 text-green-700 text-xs' : 'bg-blue-100 text-blue-700 text-xs'}>
                            {u.user_type === 'producer' ? 'Producteur' : 'Consommateur'}
                          </Badge>
                        </td>
                        <td className="py-2 px-3 text-right">
                          <Button
                            size="sm"
                            disabled={!selectedPro || loading}
                            onClick={() => onAssignSponsor(u.id, selectedPro)}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg"
                            data-testid={`assign-sponsor-${u.id}`}
                          >
                            <UserPlus className="w-3 h-3 mr-1" /> Parrainer
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{sponsorships.length}</p>
                <p className="text-xs text-muted-foreground">Professionnels</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <Link2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{sponsorships.reduce((a, s) => a + s.referral_count, 0)}</p>
                <p className="text-xs text-muted-foreground">Filleuls total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                <Euro className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{sponsorships.reduce((a, s) => a + s.total_commission_eur, 0).toFixed(0)}€</p>
                <p className="text-xs text-muted-foreground">Commissions totales</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <Zap className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{sponsorships.reduce((a, s) => a + s.total_energy_kwh, 0).toFixed(0)}</p>
                <p className="text-xs text-muted-foreground">kWh via filleuls</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pro list with expandable referrals */}
      {sponsorships.length === 0 ? (
        <Card className="border border-slate-100">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-purple-50 border border-purple-100 flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-purple-400" />
            </div>
            <p className="text-muted-foreground font-medium">Aucun professionnel enregistre</p>
            <p className="text-sm text-muted-foreground mt-1">Creez un compte professionnel pour activer le parrainage</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sponsorships.map((s) => (
            <Card key={s.pro.id} className="bg-white border" data-testid={`sponsorship-card-${s.pro.id}`}>
              <CardContent className="p-0">
                {/* Pro header */}
                <div
                  className="flex items-center justify-between p-5 cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => setExpanded(expanded === s.pro.id ? null : s.pro.id)}
                  data-testid={`expand-pro-${s.pro.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-100 to-purple-50 flex items-center justify-center">
                      <span className="text-lg font-bold text-purple-700">{s.pro.name.charAt(0)}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{s.pro.name}</p>
                        <Badge className="bg-amber-100 text-amber-700 text-xs">Pro</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{s.pro.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right hidden sm:block">
                      <p className="text-sm font-medium">{s.referral_count} filleuls</p>
                      <p className="text-xs text-muted-foreground">{s.producer_count} prod. / {s.consumer_count} cons.</p>
                    </div>
                    <div className="text-right hidden md:block">
                      <p className="text-sm font-bold text-green-600">{s.total_commission_eur.toFixed(2)}€</p>
                      <p className="text-xs text-muted-foreground">commission</p>
                    </div>
                    {expanded === s.pro.id ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                  </div>
                </div>

                {/* Expanded referral list */}
                {expanded === s.pro.id && (
                  <div className="border-t px-5 pb-5">
                    <div className="grid grid-cols-3 gap-4 py-4">
                      <div className="text-center p-3 bg-green-50 rounded-xl">
                        <p className="text-lg font-bold text-green-700">{s.total_energy_kwh.toFixed(0)} kWh</p>
                        <p className="text-xs text-green-600">Energie echangee</p>
                      </div>
                      <div className="text-center p-3 bg-amber-50 rounded-xl">
                        <p className="text-lg font-bold text-amber-700">{s.total_margin_eur.toFixed(2)}€</p>
                        <p className="text-xs text-amber-600">Marge totale</p>
                      </div>
                      <div className="text-center p-3 bg-purple-50 rounded-xl">
                        <p className="text-lg font-bold text-purple-700">{s.total_commission_eur.toFixed(2)}€</p>
                        <p className="text-xs text-purple-600">Commission (50%)</p>
                      </div>
                    </div>
                    {s.referrals.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Filleul</th>
                              <th className="text-left py-2 px-3 font-medium text-muted-foreground hidden sm:table-cell">Email</th>
                              <th className="text-center py-2 px-3 font-medium text-muted-foreground">Type</th>
                              <th className="text-right py-2 px-3 font-medium text-muted-foreground">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {s.referrals.map(r => (
                              <tr key={r.id} className="border-b hover:bg-muted/30" data-testid={`referral-row-${r.id}`}>
                                <td className="py-2 px-3 font-medium">{r.name}</td>
                                <td className="py-2 px-3 text-muted-foreground hidden sm:table-cell">{r.email}</td>
                                <td className="py-2 px-3 text-center">
                                  <Badge className={r.user_type === 'producer' ? 'bg-green-100 text-green-700 text-xs' : 'bg-blue-100 text-blue-700 text-xs'}>
                                    {r.user_type === 'producer' ? 'Producteur' : 'Consommateur'}
                                  </Badge>
                                </td>
                                <td className="py-2 px-3 text-right">
                                  <Button
                                    size="sm" variant="ghost"
                                    className="text-red-500 hover:bg-red-50 text-xs"
                                    onClick={(e) => { e.stopPropagation(); onRemoveSponsor(r.id); }}
                                    data-testid={`remove-sponsor-${r.id}`}
                                  >
                                    <Unlink className="w-3 h-3 mr-1" /> Retirer
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">Aucun filleul pour ce professionnel</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
